export type JobRole = "manager" | "chef" | "front-desk" | "housekeeper" | "bartender" | "other";
export type JobType = "Full-time" | "Part-time" | "Contract" | "Temporary" | "Internship" | "Apprenticeship" | "Seasonal" | "Casual" | "Volunteer" | "Other";
export type Job = { id: number; title: string; company: string; location: string; type: JobType; desc: string; role: JobRole; salary?: number; salaryMax?: number; applicationMethod?: string; applicationUrl?: string };
export type PublicJobDetail = { id: number; title: string; company: string; location: string; location_country?: string; type: string; description: string; requirements: string; posted_at?: string; closes_on?: string; application_method?: string; application_url?: string };

export function normalizeJobRole(value: string): JobRole | "" {
  const title = value.toLowerCase();
  if (title === "other") return "other";
  if (/housekeep|housekeeping|room attendant/.test(title)) return "housekeeper";
  if (/chef|kitchen|cook|commis|culinary|f&b/.test(title)) return "chef";
  if (/front[ -]?(desk|office)|reception|concierge/.test(title)) return "front-desk";
  if (/bartend|bar staff|bar manager/.test(title)) return "bartender";
  if (/manag|director|supervisor/.test(title)) return "manager";
  return "";
}

export function normalizeJobType(value: string): JobType {
  const key = value.toLowerCase().replace(/[ _-]/g, "");
  const types: Record<string, JobType> = { fulltime: "Full-time", parttime: "Part-time", contract: "Contract", contractor: "Contract", temporary: "Temporary", internship: "Internship", intern: "Internship", apprenticeship: "Apprenticeship", seasonal: "Seasonal", casual: "Casual", volunteer: "Volunteer" };
  return types[key] || "Other";
}

export function safeEmployerUrl(value: unknown): string | null {
  if (typeof value !== "string" || /[\\\u0000-\u0020\u007f]/.test(value)) return null;
  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname || url.username || url.password) return null;
    return url.href;
  } catch { return null; }
}

export function publicJobPath(title: string, id: number): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "job";
  return `/public/jobs/${slug}-${id}`;
}

export function normalizePublicJobs(rows: unknown): Job[] {
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row) => {
    if (!row || typeof row !== "object") return [];
    const id = Number(row.id);
    const title = String(row.title || "").trim();
    const company = String(row.company || "").trim();
    if (!Number.isSafeInteger(id) || id <= 0 || !title || !company) return [];
    return [{ id, title, company, location: String(row.location || "").trim() || "Location pending", type: normalizeJobType(String(row.type || "")), desc: String(row.description || row.requirements || "").trim(), role: normalizeJobRole(title) || "other", applicationMethod: String(row.application_method || "cybo").toLowerCase(), applicationUrl: safeEmployerUrl(row.application_url) || undefined }];
  });
}

export function jobIsoDate(value: string | undefined, endOfDay = false): string | null {
  if (typeof value !== "string" || !value) return null;
  const trimmed = value.trim();
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  let normalized = dateOnly ? trimmed + (endOfDay ? "T23:59:59Z" : "T00:00:00Z")
    : trimmed.replace(" ", "T").replace(/(\.\d{3})\d+/, "$1").replace(/([+-]\d{2})$/, "$1:00");
  if (!dateOnly && !/(Z|[+-]\d{2}:\d{2})$/.test(normalized)) normalized += "Z";
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  if (dateOnly && parsed.toISOString().slice(0, 10) !== trimmed) return null;
  return parsed.toISOString();
}

const countryCodes = "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ");
const countryNames = new Map<string, string>();
const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
for (const code of countryCodes) {
  countryNames.set(code.toLowerCase(), code);
  countryNames.set((displayNames.of(code) || code).toLowerCase(), code);
}
for (const [name, code] of [["usa", "US"], ["united states of america", "US"], ["uk", "GB"], ["south korea", "KR"], ["north korea", "KP"], ["tanzania", "TZ"]]) countryNames.set(name, code);

export function jobLocationAddress(job: Pick<PublicJobDetail, "location" | "location_country">) {
  const parts = job.location.split(",").map((part) => part.trim()).filter(Boolean);
  const explicitCountry = countryNames.get((job.location_country || "").trim().toLowerCase());
  const suffixCountry = countryNames.get((parts.at(-1) || "").toLowerCase());
  const country = explicitCountry || suffixCountry;
  if (!country) return null;
  const locality = suffixCountry ? parts.slice(0, -1).join(", ") : job.location;
  return { "@type": "PostalAddress", addressCountry: country, ...(locality ? { addressLocality: locality } : {}) };
}

export function jobPostingSchema(job: PublicJobDetail, origin: string) {
  const datePosted = jobIsoDate(job.posted_at);
  const description = [job.description, job.requirements].filter(Boolean).join("\n\n");
  const address = jobLocationAddress(job);
  // Do not emit an incomplete rich-result claim for legacy records.
  if (!job.title || !job.company || !address || !description || !datePosted) return null;
  const types: Record<JobType, string> = { "Full-time": "FULL_TIME", "Part-time": "PART_TIME", Contract: "CONTRACTOR", Temporary: "TEMPORARY", Internship: "INTERN", Apprenticeship: "OTHER", Seasonal: "TEMPORARY", Casual: "OTHER", Volunteer: "VOLUNTEER", Other: "OTHER" };
  const validThrough = jobIsoDate(job.closes_on, true);
  const descriptionHtml = description.split(/\n\s*\n/).map((paragraph) => `<p>${paragraph.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>`).join("");
  return { "@context": "https://schema.org", "@type": "JobPosting", title: job.title, description: descriptionHtml, datePosted,
    url: origin + publicJobPath(job.title, job.id), identifier: { "@type": "PropertyValue", name: "Cybo One", value: String(job.id) },
    hiringOrganization: { "@type": "Organization", name: job.company }, employmentType: types[normalizeJobType(job.type)],
    jobLocation: { "@type": "Place", address },
    ...(validThrough ? { validThrough } : {}),
  };
}
